import React, { useCallback, useEffect, useState } from "react";
import { hotels } from "../api/endpoints";
import type { ManagementCompany } from "../types";

export default function ManagementCompaniesPage() {
  const [companies, setCompanies] = useState<ManagementCompany[]>([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editMC, setEditMC] = useState<ManagementCompany | null>(null);
  const [form, setForm] = useState({ name: "", contact_name: "", phone: "", email: "", mobile: "", alt_contact_name: "", alt_contact_email: "", address: "", city: "", state: "", zip_code: "" });

  const load = useCallback(() => {
    const params = search ? `search=${search}` : "";
    hotels.managementCompanies.list(params).then((res) => setCompanies(res.items));
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editMC) {
      await hotels.managementCompanies.update(editMC.id, form);
    } else {
      await hotels.managementCompanies.create(form);
    }
    setShowForm(false);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Management Companies</h1>
        <button onClick={() => { setEditMC(null); setForm({ name: "", contact_name: "", phone: "", email: "", mobile: "", alt_contact_name: "", alt_contact_email: "", address: "", city: "", state: "", zip_code: "" }); setShowForm(true); }} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Add Company
        </button>
      </div>

      <input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="border rounded px-3 py-2 w-full mb-4" />

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Contact</th>
              <th className="px-4 py-3 text-left">Phone</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((mc) => (
              <tr key={mc.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{mc.name}</td>
                <td className="px-4 py-3">{mc.contact_name}</td>
                <td className="px-4 py-3">{mc.phone}</td>
                <td className="px-4 py-3">{mc.email}</td>
                <td className="px-4 py-3">
                  <button onClick={() => { setEditMC(mc); setForm({ name: mc.name, contact_name: mc.contact_name, phone: mc.phone, email: mc.email, mobile: mc.mobile || "", alt_contact_name: mc.alt_contact_name || "", alt_contact_email: mc.alt_contact_email || "", address: mc.address, city: mc.city, state: mc.state, zip_code: mc.zip_code }); setShowForm(true); }} className="text-blue-600 hover:underline text-xs mr-2">Edit</button>
                  <button onClick={async () => { if (window.confirm("Deactivate?")) { await hotels.managementCompanies.delete(mc.id); load(); } }} className="text-red-600 hover:underline text-xs">Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">{editMC ? "Edit Company" : "Add Company"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                <input placeholder="Company Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-3 py-2" required />
                <input placeholder="Contact Name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="w-full border rounded px-3 py-2" />
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border rounded px-3 py-2" />
                <input placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full border rounded px-3 py-2" />
                <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded px-3 py-2" />
                <input placeholder="Alt Contact Name" value={form.alt_contact_name} onChange={(e) => setForm({ ...form, alt_contact_name: e.target.value })} className="w-full border rounded px-3 py-2" />
                <input placeholder="Alt Contact Email" type="email" value={form.alt_contact_email} onChange={(e) => setForm({ ...form, alt_contact_email: e.target.value })} className="w-full border rounded px-3 py-2" />
                <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border rounded px-3 py-2" />
                <div className="grid grid-cols-3 gap-2">
                  <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="border rounded px-3 py-2" />
                  <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="border rounded px-3 py-2" />
                  <input placeholder="Zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="border rounded px-3 py-2" />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
